import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Search, TrendingUp, Activity, BarChart3, Grid3x3, List, Columns } from 'lucide-react';
import { getCurrencyCode, getCurrencySymbol, getCurrencySymbolByCode } from '../countryConfig';
import { usePortfolioPrices } from '../hooks/usePricing';
import { CompactPriceDisplay } from './PriceDisplay';
import { CompactGainLossIndicator, InlineGainLoss } from './GainLossIndicator';
import { formatCurrency, formatLastUpdated } from '../utils/priceCalculations';

/**
 * TopHoldings Component - Professional holdings display with filtering, sorting, and multiple views
 * Features:
 * - Category filtering (All, Stocks, Crypto, ETFs, Bonds, etc.)
 * - Show More/Less collapsible functionality
 * - Sort options (Value, Name, % of Portfolio, Return)
 * - Density toggle (Compact, Comfortable, Spacious)
 * - Quick stats summary bar
 * - Fully mobile responsive
 */
const TopHoldings = ({
  investments = [],
  cryptocurrencies = [],
  totalValue = 0,
  currencySymbol = '$',
  isDark = false,
  exchangeRates = null,
  selectedCountry = 'US'
}) => {
  const [showAll, setShowAll] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [sortBy, setSortBy] = useState('value'); // value, name, percentage, return
  const [density, setDensity] = useState('comfortable'); // compact, comfortable, spacious
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch real-time prices for all investments
  const { prices, loading: pricesLoading, lastUpdated } = usePortfolioPrices(investments);

  // Combine and categorize all holdings
  const allHoldings = useMemo(() => {
    const combined = [
      ...investments.map(inv => ({
        ...inv,
        type: 'investment',
        displayCategory: inv.category || 'Other'
      })),
      ...cryptocurrencies.map(crypto => ({
        id: crypto.id,
        name: crypto.name || crypto.symbol,
        amount: crypto.value || crypto.balance || 0,
        category: 'Cryptocurrency',
        displayCategory: 'Crypto',
        return: crypto.expectedReturn || 10,
        type: 'crypto'
      }))
    ];
    return combined;
  }, [investments, cryptocurrencies]);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = new Set(allHoldings.map(h => h.displayCategory));
    return ['All', ...Array.from(cats).sort()];
  }, [allHoldings]);

  // Get category counts
  const categoryCounts = useMemo(() => {
    const counts = { All: allHoldings.length };
    allHoldings.forEach(h => {
      counts[h.displayCategory] = (counts[h.displayCategory] || 0) + 1;
    });
    return counts;
  }, [allHoldings]);

  // Filter, search, and sort holdings
  const processedHoldings = useMemo(() => {
    let filtered = allHoldings;

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(h => h.displayCategory === selectedCategory);
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(h =>
        h.name?.toLowerCase().includes(query) ||
        h.displayCategory?.toLowerCase().includes(query)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'value':
          return b.amount - a.amount;
        case 'name':
          return (a.name || '').localeCompare(b.name || '');
        case 'percentage':
          const pctA = totalValue > 0 ? (a.amount / totalValue) * 100 : 0;
          const pctB = totalValue > 0 ? (b.amount / totalValue) * 100 : 0;
          return pctB - pctA;
        case 'return':
          return (b.return || 0) - (a.return || 0);
        default:
          return 0;
      }
    });

    return filtered;
  }, [allHoldings, selectedCategory, searchQuery, sortBy, totalValue]);

  // Display holdings (show 5 or all based on toggle)
  const displayedHoldings = showAll ? processedHoldings : processedHoldings.slice(0, 5);
  const hiddenCount = processedHoldings.length - displayedHoldings.length;

  // Calculate stats for summary bar
  const topHolding = processedHoldings[0];
  const topHoldingPercentage = topHolding && totalValue > 0
    ? ((topHolding.amount / totalValue) * 100).toFixed(1)
    : 0;

  // Density styles
  const densityStyles = {
    compact: {
      padding: 'p-2',
      spacing: 'space-y-2',
      fontSize: 'text-sm',
      iconSize: 'w-6 h-6',
      badgeSize: 'w-6 h-6 text-xs'
    },
    comfortable: {
      padding: 'p-4',
      spacing: 'space-y-4',
      fontSize: 'text-base',
      iconSize: 'w-8 h-8',
      badgeSize: 'w-8 h-8 text-sm'
    },
    spacious: {
      padding: 'p-6',
      spacing: 'space-y-6',
      fontSize: 'text-lg',
      iconSize: 'w-10 h-10',
      badgeSize: 'w-10 h-10 text-base'
    }
  };

  const currentDensity = densityStyles[density];

  // ⭐ MULTI-CURRENCY: Helper function to format amount with currency conversion
  const formatHoldingAmount = (holding) => {
    const investmentCurrency = holding.currency || getCurrencyCode(selectedCountry);
    const displayCurrency = getCurrencyCode(selectedCountry);
    const amount = holding.amount;

    // If same currency, just show once
    if (investmentCurrency === displayCurrency) {
      const symbol = getCurrencySymbol(selectedCountry);
      return `${symbol}${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    }

    // Different currency - show native + converted
    if (exchangeRates && exchangeRates.rates) {
      try {
        const fromRate = exchangeRates.rates[investmentCurrency];
        const toRate = exchangeRates.rates[displayCurrency];
        if (fromRate && toRate) {
          const converted = (amount / fromRate) * toRate;
          const nativeSymbol = getCurrencySymbolByCode(investmentCurrency);
          const displaySymbol = getCurrencySymbol(selectedCountry);
          return (
            <>
              <span className="text-blue-600 dark:text-blue-400">
                {nativeSymbol}{amount.toLocaleString(undefined, { maximumFractionDigits: 0 })} {investmentCurrency}
              </span>
              <br />
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ≈ {displaySymbol}{Math.round(converted).toLocaleString()}
              </span>
            </>
          );
        }
      } catch (e) {
        // Fallback
      }
    }

    // Fallback: show native currency
    const nativeSymbol = getCurrencySymbolByCode(investmentCurrency);
    return `${nativeSymbol}${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${investmentCurrency}`;
  };

  // Category icon colors
  const getCategoryColor = (category) => {
    const colors = {
      'Stocks': 'from-blue-500 to-blue-600',
      'ETF': 'from-green-500 to-green-600',
      'Crypto': 'from-orange-500 to-orange-600',
      'Cryptocurrency': 'from-orange-500 to-orange-600',
      'Bonds': 'from-purple-500 to-purple-600',
      'Real Estate': 'from-teal-500 to-teal-600',
      'Other': 'from-gray-500 to-gray-600'
    };
    return colors[category] || 'from-blue-500 to-purple-600';
  };

  if (allHoldings.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-100 dark:border-gray-700">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Top Holdings</h3>
        <div className="flex flex-col items-center justify-center h-48 text-center">
          <Activity className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-400 mb-2">No holdings yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-500">Add investments to see your portfolio breakdown</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-8 shadow-lg border border-gray-100 dark:border-gray-700">
      {/* Header with Title and Controls */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
            Top Holdings
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
              ({processedHoldings.length} {processedHoldings.length === 1 ? 'item' : 'items'})
            </span>
          </h3>

          {/* Density Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setDensity('compact')}
              className={`p-2 rounded transition-colors ${
                density === 'compact'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
              title="Compact view"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDensity('comfortable')}
              className={`p-2 rounded transition-colors ${
                density === 'comfortable'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
              title="Comfortable view"
            >
              <Columns className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDensity('spacious')}
              className={`p-2 rounded transition-colors ${
                density === 'spacious'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
              title="Spacious view"
            >
              <Grid3x3 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Quick Stats Summary Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Total Value</p>
                <p className="text-xl font-bold text-blue-900 dark:text-blue-100">
                  {currencySymbol}{totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-blue-600 dark:text-blue-400 opacity-50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-1">Holdings</p>
                <p className="text-xl font-bold text-green-900 dark:text-green-100">
                  {allHoldings.length} items
                </p>
              </div>
              <Activity className="w-8 h-8 text-green-600 dark:text-green-400 opacity-50" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-4 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-purple-600 dark:text-purple-400 mb-1">Top Asset</p>
                <p className="text-sm font-bold text-purple-900 dark:text-purple-100 truncate">
                  {topHolding?.name || 'N/A'}
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400">
                  {topHoldingPercentage}% of portfolio
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-600 dark:text-purple-400 opacity-50" />
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search holdings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
          />
        </div>

        {/* Category Filter Pills */}
        <div className="flex flex-wrap gap-2 mb-4">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === cat
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {cat} ({categoryCounts[cat] || 0})
            </button>
          ))}
        </div>

        {/* Sort Options */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-gray-600 dark:text-gray-400 font-medium">Sort by:</span>
          {[
            { key: 'value', label: 'Value' },
            { key: 'name', label: 'Name' },
            { key: 'percentage', label: '% of Portfolio' },
            { key: 'return', label: 'Return' }
          ].map(option => (
            <button
              key={option.key}
              onClick={() => setSortBy(option.key)}
              className={`px-3 py-1 rounded-lg transition-colors ${
                sortBy === option.key
                  ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Holdings List */}
      {processedHoldings.length === 0 ? (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">No holdings match your search</p>
          <button
            onClick={() => {
              setSearchQuery('');
              setSelectedCategory('All');
            }}
            className="mt-3 text-blue-600 dark:text-blue-400 hover:underline text-sm"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <>
          <div className={currentDensity.spacing}>
            {displayedHoldings.map((holding, index) => {
              const percentage = totalValue > 0 ? ((holding.amount / totalValue) * 100).toFixed(1) : 0;
              const actualIndex = processedHoldings.indexOf(holding);

              return (
                <div
                  key={holding.id}
                  className={`flex items-center space-x-3 sm:space-x-4 ${currentDensity.padding} bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors`}
                >
                  {/* Rank Badge */}
                  <div className={`${currentDensity.badgeSize} bg-gradient-to-br ${getCategoryColor(holding.displayCategory)} rounded-lg flex items-center justify-center text-white font-bold flex-shrink-0`}>
                    {actualIndex + 1}
                  </div>

                  {/* Holding Info */}
                  <div className="flex-1 min-w-0">
                    <p className={`font-semibold text-gray-900 dark:text-white truncate ${currentDensity.fontSize}`}>
                      {holding.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className={`text-gray-600 dark:text-gray-400 ${density === 'compact' ? 'text-xs' : 'text-sm'}`}>
                        {holding.displayCategory}
                      </p>
                      {holding.ticker && prices[holding.ticker] && (
                        <span className={`text-xs text-gray-500 dark:text-gray-400`}>
                          • <CompactPriceDisplay
                            price={prices[holding.ticker].price}
                            currency={prices[holding.ticker].currency || holding.currency}
                            className="inline"
                          />
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Amount, Percentage & Gain/Loss */}
                  <div className="text-right flex-shrink-0">
                    <p className={`font-bold text-gray-900 dark:text-white ${currentDensity.fontSize}`}>
                      {formatHoldingAmount(holding)}
                    </p>
                    <p className={`text-gray-600 dark:text-gray-400 ${density === 'compact' ? 'text-xs' : 'text-sm'}`}>
                      {percentage}% of portfolio
                    </p>
                    {holding.ticker && prices[holding.ticker] && holding.quantity && holding.averageCostBasis && (
                      <div className="mt-1">
                        <InlineGainLoss
                          currentValue={holding.quantity * prices[holding.ticker].price}
                          costBasis={holding.quantity * holding.averageCostBasis}
                          currency={holding.currency || getCurrencyCode(selectedCountry)}
                          className="text-xs"
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Show More/Less Button */}
          {processedHoldings.length > 5 && (
            <div className="mt-6 text-center">
              <button
                onClick={() => setShowAll(!showAll)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                {showAll ? (
                  <>
                    <ChevronUp className="w-5 h-5" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-5 h-5" />
                    Show {hiddenCount} More {hiddenCount === 1 ? 'Holding' : 'Holdings'}
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TopHoldings;
